import ReactPDF, { StyleSheet, Font } from '@react-pdf/renderer';
import { FontOption } from '../../../types/badge';
import { FONT } from '../../../types/enums';

Font.registerHyphenationCallback((word) => [word]);

export const borderStyle = '1px dashed #979797';
const arrowColor = '#9b989c';

export const registerFonts = (fonts?: FontOption[]) => {
  const requiredFallbacks: Array<{ family: FONT; src?: string; fonts?: { src: string }[] }> = [
    {
      family: FONT.CHINESE_SIMPLIFIED,
      src: 'https://fonts.gstatic.com/s/zcoolxiaowei/v14/i7dMIFFrTRywPpUVX9_RJyM1YFKQHwyVd3U.ttf'
    },
    {
      family: FONT.JAPANESE,
      fonts: [
        { src: 'https://fonts.gstatic.com/s/notosanssc/v38/k3kCo84MPvpLmixcA63oeAL7Iqp5IZJF9bmaG9_FnYxNbPzS5HE.ttf' },
        { src: 'https://fonts.gstatic.com/s/notosansjp/v52/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFBEj75vY0rw-oME.ttf' }
      ]
    },
    {
      family: FONT.JAPANESE_BOLD_800,
      src: 'https://fonts.gstatic.com/s/notosansjp/v52/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFJEk75vY0rw-oME.ttf'
    },
    {
      family: FONT.NOTO_SANS,
      src: 'https://fonts.gstatic.com/ea/nanumgothic/v5/NanumGothic-ExtraBold.ttf'
    },
    {
      family: FONT.NOTO_SANS_TMP,
      src: 'https://fonts.gstatic.com/s/notosans/v39/o-0mIpQlx3QUlC5A4PNB6Ryti20_6n1iPHjcz6L1SoM-jCpoiyD9A99d41P6zHtY.ttf'
    }
  ];

  const customFamilies = new Set<string>(fonts?.map((f) => f.name) || []);

  if (fonts && fonts.length > 0) {
    fonts.forEach((font) => {
      try {
        if (font?.src && !font?.fonts) {
          Font.register({ family: font.name, src: font.src });
        } else if (!font?.src && font?.fonts?.length) {
          Font.register({ family: font.name, fonts: font.fonts });
        }
      } catch (e) {
        console.error(`Failed to register custom font '${font?.name}':`, e);
      }
    });
  }

  requiredFallbacks.forEach((fb) => {
    if (!customFamilies.has(fb.family)) {
      try {
        if (fb.src) {
          Font.register({ family: fb.family, src: fb.src });
        } else if (fb.fonts) {
          Font.register({ family: fb.family, fonts: fb.fonts });
        }
      } catch (e) {
        console.error(`Failed to register fallback font '${fb.family}':`, e);
      }
    }
  });
};

const row: ReactPDF.Styles = {
  default: {
    display: 'flex',
    flexDirection: 'row',
    height: '50%'
  }
};

const eventImage = {
  borderBottom: 'none'
};

const pdfStyles: ReactPDF.Styles = StyleSheet.create({
  row2Reverse: row.reverse,
  rotatedItem: {
    transform: 'rotate(180deg)',
    border: 'none'
  },
  reverseRotatedBadge: {
    transform: 'rotate(180deg)'
  },
  noBorderBottom: {
    borderBottom: 'none'
  },
  qrCodeFullWidth: {
    minHeight: 90,
    minWidth: 90,
    marginTop: 23
  },
  badgeItemFullWidth: {
    width: '100%',
    maxWidth: '100%'
  },
  badgeNameMarginTop: {
    marginTop: 14
  },
  row4x5Inches: {
    ...row.default,
    borderBottom: 'none',
    height: '100%'
  },
  row: row.default,
  row2: {
    ...row.default,
    borderBottom: 'none'
  },
  japaneseBadgeItem: {
    padding: 0,
    paddingBottom: 12,
    width: '50%',
    maxWidth: '50%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  badgeItem: {
    width: '50%',
    maxWidth: '50%',
    padding: 12,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  secondRowItem: {
    ...eventImage
  },
  thirdRowItem: {
    ...eventImage
  },
  eventImage: {
    height: '100%',
    width: '100%',
    minHeight: 50
  },
  eventLogo: {
    width: '100%',
    minHeight: 50,
    maxHeight: 80
  },
  name: {
    fontSize: 24,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'left',
    minHeight: 8
  },
  position: {
    marginTop: 16,
    minHeight: 10,
    fontSize: 16,
    textAlign: 'center',
    width: '100%'
  },
  company: {
    fontSize: 16,
    marginTop: 16,
    minHeight: 10,
    width: '100%',
    textAlign: 'center'
  },
  qrCode: {
    marginTop: 20,
    minHeight: 120,
    minWidth: 120
  },
  qrCode298x100: {
    marginTop: 20,
    maxHeight: 120,
    maxWidth: 120
  },
  qrCodeLetterSize: {
    minHeight: 115,
    minWidth: 115
  },
  twoLinesQrCodeLetterSize: {
    minHeight: 105,
    minWidth: 105
  },
  id: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    marginTop: 2
  },
  customerType4x10Inches: {
    marginTop: 16
  },
  customerTypeWrapper: {
    marginTop: 46,
    backgroundColor: '#000',
    minHeight: 30,
    padding: '0',
    minWidth: '100%',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  customerTypeText: {
    color: '#fff',
    textTransform: 'uppercase',
    fontFamily: 'Helvetica-Bold',
    fontSize: 20,
    textAlign: 'center',
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: 50
  },
  customerTypeWrapperLeft: {
    backgroundColor: '#000',
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    left: 0,
    bottom: 0,
    minWidth: 50,
    minHeight: 50,
    borderTopRightRadius: 30
  },
  customerTypeTextLeft: {
    marginBottom: -1,
    marginLeft: -7,
    color: '#fff',
    textTransform: 'uppercase',
    fontFamily: 'Helvetica-Bold',
    fontSize: 38,
    display: 'flex',
    alignSelf: 'center'
  },
  normalFontSize: {
    fontSize: 18
  },
  smallFontSize: {
    fontSize: 15
  },
  verySmallFontSize: {
    fontSize: 12
  },
  tinyFontSize: {
    fontSize: 10
  },
  extraTinyFontSize: {
    fontSize: 8
  },
  nameView: {
    flexDirection: 'column',
    padding: '18px 0 28px 0',
    alignItems: 'center',
    marginBottom: 8
  },
  letterNameView: { padding: '12px 0 28px 0' },
  firstArrow: {
    height: 15,
    width: 15
  },
  firstArrowWrapper: {
    position: 'absolute',
    top: '46%',
    left: '48.5%',
    backgroundColor: '#fff',
    width: 19,
    height: 20,
    paddingTop: 2.5
  },
  firstArrowText: {
    position: 'absolute',
    fontSize: 13,
    top: -1,
    left: 10,
    color: arrowColor,
    fontFamily: 'Helvetica-Bold'
  },
  secondArrow: {
    height: 15,
    width: 15
  },
  secondArrowWrapper: {
    position: 'absolute',
    top: '55%',
    left: '49%',
    width: 19,
    height: 25,
    backgroundColor: '#fff',
    paddingTop: 2.5
  },
  secondArrowText: {
    position: 'absolute',
    fontSize: 13,
    top: 9,
    left: 5,
    color: arrowColor,
    fontFamily: 'Helvetica-Bold'
  },
  address: {
    paddingTop: 20,
    fontSize: 16
  },
  addressWithReducedPaddingTop: {
    paddingTop: 10
  },
  oneIconContainer: {
    top: '83%'
  },
  twoIconContainer: {
    top: '72%'
  },
  threeIconContainer: {
    top: '61%'
  },
  iconContainer: {
    position: 'absolute',
    right: '5%'
  },
  defaultDividerContainer: {
    position: 'absolute',
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  halfFlex: { flex: '1 1 50%' },
  dividerHeight: {
    height: '50%'
  },
  downscaleTo80Percents: { transform: 'scale(0.8)' },
  relative: { position: 'relative' }
});

export default pdfStyles;
